/**
 * Renders ONE node and recurses into its children.
 *
 * The DOM tree mirrors the document tree exactly: every node is absolutely
 * positioned inside its parent's content box, so moving a frame moves its whole
 * subtree with no extra bookkeeping. Children of an auto-layout frame are the
 * one exception — they are laid out by flex, so their x/y are dropped (see
 * `flowChildStyles`).
 *
 * This component is presentational. It raises pointer intent through
 * `nodePointerDown`; the canvas owns the gesture state machine.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';

import type { DesignNode } from '../../design-builder.types';
import { flowChildStyles, nodeStyles } from '../../utils/design-style.utils';
import { DesignWidgetHostComponent } from '../../renderers/design-widget-host.component';
import { iconDataFor } from '../icon-library/design-icon-catalogue';
import { iconActionLabel, normalizeIconAction } from '../../utils/design-icon-actions.utils';
import { DesignDataService, formatBoundValue, initialsOf, renderBoundText, type NumberFormat } from '../../services/design-data.service';
import { convertCurrencyValue } from '../../utils/design-currency.utils';
import { COLUMN_LIMIT_PREFIX, DesignRuntimeService } from '../../data/design-runtime.service';
import { DesignDocumentStore } from '../../state/design-document.store';
import { findNode } from '../../utils/design-node.utils';
import { DesignFitTextDirective } from './design-fit-text.directive';
import { numericValue, resolveValueColorRanges, valueColorFor } from '../../utils/design-value-color.utils';
import { followPosition, stopShift, nodeNumber, slideLeaderOf, slidePosition, type BoundValueReader, type FormulaValueReader } from '../../utils/design-slide.utils';

export interface NodePointerEvent {
  node: DesignNode;
  event: PointerEvent;
}

@Component({
  selector: 'app-design-node-view',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, LucideAngularModule, DesignWidgetHostComponent, DesignFitTextDirective],
  templateUrl: './design-node-view.component.html',
  styleUrl: './design-node-view.component.scss',
})
export class DesignNodeViewComponent {
  readonly node = input.required<DesignNode>();
  /** True when the PARENT runs auto-layout, so this node is flex-positioned. */
  readonly inFlow = input<boolean>(false);
  /** The node's siblings (its own parent's children) — "Move with value" stops live here. */
  readonly siblings = input<readonly DesignNode[]>([]);
  readonly selectedIds = input<ReadonlySet<string>>(new Set());
  /** Frame currently highlighted as a palette drop target. */
  readonly dropTargetId = input<string | null>(null);
  /** Suppress live widget rendering while a gesture is in flight. */
  readonly suspended = input<boolean>(false);
  /** Data mode: show unbound widgets and text as placeholders, not fakes. */
  readonly hideSample = input<boolean>(false);
  /** Actions are enabled only in dashboard Preview / published screens. */
  readonly interactive = input(false);
  readonly iconAction = output<DesignNode>();
  readonly iconFileDrop = output<{ node: DesignNode; files: File[] }>();
  readonly fileDragActive = signal(false);
  readonly canDropFile = computed(() => this.node().type === 'ICON' && this.hasAction() && this.node().icon?.action === 'upload');

  onFileDragOver(event: DragEvent): void {
    if (!this.canDropFile() || !event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    this.fileDragActive.set(true);
  }

  onFileDragLeave(event: DragEvent): void {
    if ((event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) return;
    this.fileDragActive.set(false);
  }

  onFileDrop(event: DragEvent): void {
    this.fileDragActive.set(false);
    if (!this.canDropFile()) return;
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length) this.iconFileDrop.emit({ node: this.node(), files });
  }
  readonly actionLabel = computed(() => iconActionLabel(this.node().icon?.action));
  readonly hasAction = computed(() => this.interactive() && normalizeIconAction(this.node().icon?.action) !== 'none');

  activateIcon(event: Event): void {
    event.stopPropagation();
    if (this.hasAction()) this.iconAction.emit(this.node());
  }

  /**
   * In data mode, anything NOT backed by real data is dimmed — charts, KPI
   * numbers, bound text, all of it. Only what is genuinely connected stays in
   * full colour, so the screen reads as a checklist. Shapes, icons and frames
   * are chrome rather than data, so they are left alone.
   */
  readonly isUnboundData = computed(() => {
    if (!this.hideSample()) return false;
    const node = this.node();
    // Connected means a dataset was actually chosen. Keying off the `sample`
    // flag alone missed tables and charts whose flag was lost on a save/reload
    // round-trip, so they kept rendering in full colour while still fake.
    if (node.widget) {
      return node.widget.sample === true || !node.widget.binding?.['dataset'];
    }
    if (node.type !== 'TEXT') return false;
    if (node.formula?.expression) return this.formulaResult()?.status === 'error';
    // Text counts as connected only once its bound value has actually loaded.
    return this.boundState() !== 'ready';
  });
  /** Node being text-edited in place. */
  readonly editingTextId = input<string | null>(null);

  readonly nodePointerDown = output<NodePointerEvent>();
  readonly nodeDoubleClick = output<NodePointerEvent>();
  /** Emits the new text after an inline edit commits. */
  readonly textCommit = output<{ node: DesignNode; value: string }>();

  readonly styles = computed(() => {
    if (this.inFlow()) return flowChildStyles(this.node());
    const style = nodeStyles(this.node());
    const valueColor = this.valueColor();
    if (valueColor) style['color'] = this.node().valueColorTarget === 'pill' ? '#ffffff' : valueColor;
    const pill = this.pillColor();
    if (pill) style['background'] = pill;
    if (!this.levelMatch()) {
      // Not at its level: gone in Preview / published, faded on the canvas so it stays editable.
      if (this.interactive()) style['display'] = 'none';
      else style['opacity'] = '0.3';
    }
    const moved = this.slidePosition();
    if (moved !== null) {
      style['left'] = `${moved.x}px`;
      style['top'] = `${moved.y}px`;
      style['transition'] = 'left 300ms ease, top 300ms ease';
    }
    return style;
  });

  /**
   * "Move with value": the x this node slides to between its stops — in
   * Preview / published screens and on the canvas. On the canvas, while it (or
   * the layer carrying it) is selected or being dragged, it shows where it was
   * drawn, so the selection box and handles match what you move.
   */
  private readonly slidePosition = computed<{ x: number; y: number } | null>(() => {
    const node = this.node();
    if (this.inFlow()) return null;
    if (!this.interactive()) {
      if (this.suspended()) return null;
      const selected = this.selectedIds();
      if (selected.has(node.id)) return null;
      const leader = node.slide ? null : slideLeaderOf(node, this.siblings());
      if (leader && selected.has(leader.id)) return null;
    }
    if (node.slide?.stopIds.length) return slidePosition(node, this.siblings(), this.readBound, this.readFormula);
    // "Also move": a layer the moving one carries along.
    return followPosition(node, this.siblings(), this.readBound, this.readFormula)
      // Best / Cost (with its pill, caption, %) step aside for a value past them.
      ?? stopShift(node, this.siblings(), this.readBound, this.readFormula);
  });

  private readonly readFormula: FormulaValueReader = (node) => {
    const result = node.formula?.expression && this.data ? this.data.formula(node.formula, node.id) : null;
    return result?.status === 'ready' ? result.value : null;
  };

  private readonly readBound: BoundValueReader = (binding) => {
    const state = this.data?.value(binding);
    return state?.status === 'ready' ? state.value : null;
  };

  /** Render fixed-size widget internals at their pre-group size, then transform
   * the whole visual into its scaled child box. This prevents controls and
   * charts from spilling outside a resized group. */
  readonly widgetScaleStyles = computed<Record<string, string>>(() => {
    const scaleX = Math.max(0.01, this.node().groupScaleX ?? 1);
    const scaleY = Math.max(0.01, this.node().groupScaleY ?? 1);
    return {
      width: `${100 / scaleX}%`,
      height: `${100 / scaleY}%`,
      transform: `scale(${scaleX}, ${scaleY})`,
      'transform-origin': 'top left',
    };
  });

  /**
   * A group is a reference to its members, which draw themselves. Rendering it
   * too would put an invisible box over them that swallows every click.
   */
  readonly isReferenceGroup = computed(() => !!this.node().memberIds?.length);

  readonly isSelected = computed(() => this.selectedIds().has(this.node().id));
  readonly isDropTarget = computed(() => this.dropTargetId() === this.node().id);
  readonly isEditingText = computed(() => this.editingTextId() === this.node().id);

  // ── Data-bound text ────────────────────────────────────────────────────────
  // Optional: thumbnails / previews rendered outside the builder have no data
  // service and simply show the text as typed.
  private readonly data = inject(DesignDataService, { optional: true });
  /** Absent in thumbnails / previews, which show no editor chrome. */
  private readonly runtime = inject(DesignRuntimeService, { optional: true });
  /** The whole design, to find a layer outside this one's parent (see layerById). */
  private readonly doc = inject(DesignDocumentStore, { optional: true });

  /** With exactly one filter selected, the widgets and texts it drives are marked. */
  readonly isFilterTarget = computed(() => {
    const selected = this.selectedIds();
    if (!this.runtime || selected.size !== 1) return false;
    const [sourceId] = selected;
    return sourceId !== this.node().id && this.runtime.targetNodeIds(sourceId).has(this.node().id);
  });

  private readonly boundValue = computed(() => {
    const binding = this.node().dataBinding;
    return binding && this.data ? this.data.value(binding, this.node().id) : null;
  });

  /** A filter change is recomputing this text — its old value is stale. */
  private readonly isRefreshing = computed(() => {
    const componentId = this.node().dataBinding?.componentId;
    return !!componentId && !!this.runtime?.busy().has(componentId);
  });

  readonly boundState = computed(() =>
    this.isRefreshing() ? 'loading' : (this.boundValue()?.status ?? null),
  );

  /**
   * "Color by value": the colour of the range the text's number falls in - its
   * formula result, else its live (backend) value, else a number typed in it.
   * Re-evaluated whenever a new value arrives.
   */
  readonly valueColor = computed<string | null>(() => {
    const node = this.node();
    if (node.type !== 'TEXT' || !node.valueColors?.length) return null;
    return this.colorOf(node, this.colorNumber(node, this.textNumber()));
  });

  /** The number a "Color by value" text is judged by: its own, or the chosen sibling's (valueColorSourceId). */
  private colorNumber(text: DesignNode, own: number | null): number | null {
    if (!text.valueColorSourceId) return own;
    const source = this.layerById(text.valueColorSourceId);
    return source ? nodeNumber(source, this.readBound, this.readFormula) : null;
  }

  /**
   * A layer by id: a sibling first, else anywhere in the design - the layer a
   * colour is judged by (or a marker limit) may sit in another card or frame.
   */
  private layerById(id: string): DesignNode | null {
    const sibling = this.siblings().find((candidate) => candidate.id === id);
    if (sibling) return sibling;
    for (const page of this.doc?.document().pages ?? []) {
      const found = findNode(page.nodes, id);
      if (found) return found;
    }
    return null;
  }

  /**
   * A text's "Color by value" colour for its number. A range's From / To may be
   * a marker on the canvas (MFN, FLOOR ...): its live number is the limit, so
   * the bands follow each record's own prices - no slider, no filter involved.
   */
  private colorOf(text: DesignNode, value: number | null): string | null {
    const markerNumber = (id: string) => {
      // A limit read from a dataset column: its value for THIS text's filters,
      // so the text and its limit compare over the same rows.
      if (id.startsWith(COLUMN_LIMIT_PREFIX)) return numericValue(this.runtime?.columnValue(id, text.dataBinding?.componentId ?? '') ?? null);
      const marker = this.layerById(id);
      return marker ? nodeNumber(marker, this.readBound, this.readFormula) : null;
    };
    return valueColorFor(resolveValueColorRanges(text.valueColors, markerNumber), value);
  }

  /**
   * "Colour the pill": this shape sits right behind a text set to colour its
   * pill - it takes that text's colour (the text itself turns white).
   */
  private readonly pillColor = computed<string | null>(() => {
    const node = this.node();
    if (node.type === 'TEXT') return null;
    const owner = this.siblings().find((text) =>
      text.type === 'TEXT' && text.valueColorTarget === 'pill' && !!text.valueColors?.length && encloses(node, text));
    return owner ? this.colorOf(owner, this.colorNumber(owner, nodeNumber(owner, this.readBound, this.readFormula))) : null;
  });

  private readonly textNumber = computed<number | null>(() => {
    const node = this.node();
    if (node.formula?.expression) {
      const result = this.formulaResult();
      return result?.status === 'ready' ? result.value : null;
    }
    if (node.dataBinding) {
      const state = this.boundValue();
      return state?.status === 'ready' ? numericValue(state.value) : null;
    }
    return numericValue(node.text?.value ?? '');
  });

  /** A text formula's result, worked out from other layers in the browser. */
  readonly formulaResult = computed(() => {
    const formula = this.node().formula;
    return formula?.expression && this.data ? this.data.formula(formula, this.node().id) : null;
  });

  readonly displayText = computed(() => this.withLevelTokens(this.valueText()));

  /**
   * Zone-slider tokens in any text - they follow the handle as it moves:
   * {{level}} its current level (the block's label), {{sliderValue}} its value.
   * {{level:Name}} / {{sliderValue:Name}} read the zone slider with that layer name.
   */
  private withLevelTokens(text: string): string {
    if (!text.includes('{{') || !this.runtime) return text;
    return text.replace(/\{\{\s*(level|sliderValue)\s*(?::\s*([^}]+?))?\s*\}\}/g, (match, token: string, slider?: string) => {
      const level = this.runtime!.zoneSliderLevel(slider ?? '');
      if (!level) return match;
      return token === 'level' ? level.label : level.valueText;
    });
  }

  /**
   * "Show at levels": false while the zone slider is outside every level the
   * layer is set to show at (it is hidden in Preview / published, faded here).
   */
  private readonly levelMatch = computed(() => {
    const rule = this.node().showAtLevels;
    if (!rule?.levels.length || !this.runtime) return true;
    const level = this.runtime.zoneSliderLevel(rule.sliderId ?? '');
    return !level || rule.levels.includes(level.label);
  });

  /** Picked in a currency dropdown's "Apply to": value × its rate, its symbol in place of the prefix. */
  private formatWithCurrency(format: NumberFormat, value: number | string | null): string {
    const currency = this.runtime?.currencyFor(this.node().id) ?? null;
    if (!currency) return formatBoundValue(format, value);
    return formatBoundValue(currency.symbol ? { ...format, prefix: currency.symbol } : format, convertCurrencyValue(value, currency));
  }

  /** "Text by level": the message typed for the slider's current level, if any. */
  private readonly levelText = computed<string | null>(() => {
    const rule = this.node().levelTexts;
    if (!rule || !this.runtime) return null;
    const level = this.runtime.zoneSliderLevel(rule.sliderId ?? '');
    const message = level ? rule.texts[level.label] : undefined;
    return message && message.trim() ? message : null;
  });

  /**
   * "Initials of": the initials of the chosen text's value as it is now (e.g.
   * "TV" for talari.vs@…). Null when not set; '' while that text has no value.
   */
  private readonly initialsText = computed<string | null>(() => {
    const id = this.node().initialsOfId;
    if (!id) return null;
    const source = this.layerById(id);
    return source ? initialsOf(this.shownTextOf(source)) : '';
  });

  /** A text layer's value as shown: its live value when bound to data, else what is typed in it. */
  private shownTextOf(source: DesignNode): string {
    if (source.dataBinding && this.data) {
      const state = this.data.value(source.dataBinding, source.id);
      return state.status === 'ready' && state.value !== null && state.value !== undefined ? String(state.value) : '';
    }
    return (source.text?.value ?? '').replace(/\{\{\s*value\s*\}\}/g, '');
  }

  private readonly valueText = computed(() => {
    const node = this.node();
    const template = this.levelText() ?? node.text?.value ?? '';
    // Until the chosen text has a value, what is typed here (e.g. "JD") shows.
    const initials = this.initialsText();
    if (initials) return renderBoundText(template, initials);
    const result = this.formulaResult();
    if (result && node.formula) {
      switch (result.status) {
        case 'ready': return renderBoundText(template, this.formatWithCurrency(node.formula, result.value));
        case 'error': return renderBoundText(template, '⚠');
        default: return renderBoundText(template, '—');
      }
    }
    const binding = node.dataBinding;
    const state = this.boundValue();
    if (!binding || !state) return template;
    switch (state.status) {
      case 'ready': return renderBoundText(template, this.formatWithCurrency(binding, state.value));
      case 'error': return renderBoundText(template, '⚠');
      default: return renderBoundText(template, '');
    }
  });

  /**
   * While the value computes, the wording either side of `{{value}}` stays and
   * a spinner sits where the number will land. A template without the token is
   * the value alone, so it is just the spinner.
   */
  readonly pendingText = computed<{ before: string; after: string } | null>(() => {
    const node = this.node();
    const state = this.boundValue();
    if (node.formula?.expression || !node.dataBinding || !state) return null;
    if (!this.isRefreshing() && (state.status === 'ready' || state.status === 'error')) return null;
    const template = node.text?.value ?? '';
    const at = template.indexOf('{{value}}');
    if (at < 0) return { before: '', after: '' };
    return { before: template.slice(0, at), after: template.slice(at + '{{value}}'.length).split('{{value}}').join('') };
  });

  readonly boundTitle = computed(() => {
    const formula = this.node().formula;
    const result = this.formulaResult();
    if (formula && result) return result.status === 'error' ? `= ${formula.expression}
${result.message}` : `= ${formula.expression}`;
    const binding = this.node().dataBinding;
    const state = this.boundValue();
    if (!binding) return null;
    const source = `${binding.aggregation} of ${binding.field} · ${binding.dataset}`;
    return state?.status === 'error' ? `${source}\n${state.message}` : source;
  });

  constructor() {
    effect(() => {
      const binding = this.node().dataBinding;
      if (binding && this.data && this.boundValue()?.status === 'idle') this.data.ensureValue(binding, this.node().name ?? '');
    });
  }

  /** Children of an auto-layout frame are positioned by flex, not by x/y. */
  readonly childrenInFlow = computed(() => {
    const node = this.node();
    return node.type === 'FRAME' && !!node.layout && node.layout.mode !== 'none';
  });

  readonly children = computed(() => this.node().children ?? []);

  /** Lucide drawing data for this node's stored icon name. */
  readonly iconData = computed(() => iconDataFor(this.node().icon?.name ?? ''));

  /** Lucide draws a SQUARE svg at `size`, so a non-square node box would
   *  distort it — fit to the shorter side and let CSS centre the result. */
  readonly iconSize = computed(() => {
    const node = this.node();
    return Math.max(1, Math.round(Math.min(node.width, node.height)));
  });

  onPointerDown(event: PointerEvent): void {
    const node = this.node();
    if (this.interactive()) return;
    if (node.locked) return;
    // Stop the parent frame from claiming a click meant for this child.
    event.stopPropagation();
    this.nodePointerDown.emit({ node, event });
  }

  /** A normal click inside a group selects/moves the group, as in Figma.
   * Ctrl/Cmd-click remains the deliberate way to target a nested child. */
  onChildPointerDown(payload: NodePointerEvent): void {
    const node = this.node();
    if (node.type === 'GROUP' && !payload.event.ctrlKey && !payload.event.metaKey) {
      this.nodePointerDown.emit({ node, event: payload.event });
      return;
    }
    this.nodePointerDown.emit(payload);
  }

  onDoubleClick(event: PointerEvent): void {
    event.stopPropagation();
    this.nodeDoubleClick.emit({ node: this.node(), event });
  }

  onTextBlur(event: Event): void {
    const value = (event.target as HTMLElement).innerText ?? '';
    this.textCommit.emit({ node: this.node(), value });
  }

  trackById(_: number, node: DesignNode): string {
    return node.id;
  }
}

/** `shape` is a pill behind `text`: it contains the text and is not much bigger. */
function encloses(shape: DesignNode, text: DesignNode): boolean {
  return shape.visible !== false
    && shape.x <= text.x + 1 && shape.y <= text.y + 1
    && shape.x + shape.width >= text.x + text.width - 1
    && shape.y + shape.height >= text.y + text.height - 1
    && shape.width * shape.height <= Math.max(1, text.width * text.height) * 6;
}
